import React, { useEffect, useState } from "react";
import { Instagram, Loader2, X, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { shareWinStory, renderWinStory, STORY_THEMES } from "../lib/winStoryCard";
import { shareWinStoryVideo } from "../lib/winStoryVideo";

const KEY = "rl_story_theme";

/* "Insta Story" button → pick Royal / Festival / Emerald (live preview of the real ticket) → share; logs share for the weekly streak bonus. */
export const WinStoryButton = ({ opts, ta, testId, onShared }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null);
  const logShare = () => api.post("/share/social", { platform: "win_story" }).then(({ data }) => {
    onShared?.(data);
    if (data.bonus) toast.success(ta ? "🔥 Share streak! Bonus spin unlock ஆனது — Home-ல் சுழற்றுங்க" : "🔥 Share streak! Bonus spin unlocked — spin on Home", { duration: 6000 });
    else if (data.week_shares < data.streak_target) toast(ta ? `Story ${data.week_shares}/${data.streak_target} இந்த வாரம் · இன்னும் ${data.streak_target - data.week_shares} share பண்ணினா Bonus spin` : `Story ${data.week_shares}/${data.streak_target} this week · ${data.streak_target - data.week_shares} more for a bonus spin`);
  }).catch(() => {});
  const goVideo = async (e) => {
    e.stopPropagation();
    if (!window.MediaRecorder) { toast.error(ta ? "இந்த browser-ல் video record ஆகாது — Chrome/Safari பயன்படுத்துங்கள்" : "Video not supported in this browser — use Chrome/Safari"); return; }
    setBusy(true);
    try {
      localStorage.setItem(KEY, theme); localStorage.setItem("rl_story_sticker", sticker);
      const r = await shareWinStoryVideo({ ...opts, theme, sticker }, (s, p) => setStage({ s, p }));
      if (r === "downloaded") toast.success(ta ? "Reel video save ஆனது — Instagram Reels-ல் post பண்ணுங்க" : "Reel video saved — post it on Instagram Reels");
      logShare();
      setOpen(false);
    } catch (err) { if (err?.name !== "AbortError") toast.error(err?.response?.data?.detail || err?.message || "Video failed"); }
    setStage(null); setBusy(false);
  };
  const [theme, setTheme] = useState(localStorage.getItem(KEY) || "royal");
  const [preview, setPreview] = useState(null);
  const [sticker, setSticker] = useState(localStorage.getItem("rl_story_sticker") || "");
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const t = setTimeout(() => {
      renderWinStory({ ...opts, theme, sticker }).then((c) => { if (alive) setPreview(c.toDataURL("image/jpeg", 0.7)); }).catch(() => {});
    }, sticker ? 350 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [open, theme, sticker]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      localStorage.setItem(KEY, theme);
      localStorage.setItem("rl_story_sticker", sticker);
      const r = await shareWinStory({ ...opts, theme, sticker });
      if (r === "downloaded") toast.success(ta ? "Story படம் save ஆனது — Instagram-ல் post பண்ணுங்க" : "Story image saved — post it on Instagram");
      logShare();
      setOpen(false);
    } catch (err) { if (err?.name !== "AbortError") toast.error("Share failed"); }
    setBusy(false);
  };
  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="flex-1 text-white font-semibold py-2 rounded-xl text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform" style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }} data-testid={testId}>
        <Instagram className="w-4 h-4" />Insta Story
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end justify-center" onClick={(e) => { e.stopPropagation(); setOpen(false); }} data-testid="story-theme-sheet">
          <div className="w-full max-w-md bg-white rounded-t-3xl p-4 pb-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-black text-slate-900 text-sm">{ta ? "Story theme தேர்வு" : "Pick a Story theme"}</div>
              <button onClick={() => setOpen(false)} className="p-1 text-slate-400" data-testid="story-theme-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-3">
              <div className="w-[120px] shrink-0 aspect-[9/16] rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center shadow-lg ring-1 ring-black/10" data-testid="story-theme-preview">
                {preview ? <img src={preview} alt="Story preview" className="w-full h-full object-cover" data-testid="story-theme-preview-img" /> : <Loader2 className="w-5 h-5 text-white/60 animate-spin" />}
              </div>
              <div className="flex-1 grid grid-cols-1 gap-2 content-start">
                {Object.entries(STORY_THEMES).map(([k, t]) => (
                  <button key={k} onClick={() => setTheme(k)} className={`flex items-center gap-2.5 rounded-xl p-1.5 border-2 transition-transform active:scale-[0.98] ${theme === k ? "border-slate-900" : "border-slate-100"}`} data-testid={`story-theme-${k}`} aria-pressed={theme === k}>
                    <div className="w-10 h-10 rounded-lg shrink-0" style={{ background: t.swatch }} />
                    <div className="text-left"><div className="text-xs font-bold text-slate-900">{ta ? t.ta : t.label}</div><div className="text-[10px] text-slate-500">{theme === k ? (ta ? "தேர்வு செய்யப்பட்டது" : "Selected") : (ta ? "தொடவும்" : "Tap to preview")}</div></div>
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px] mb-1"><span className="font-bold text-slate-700">{ta ? "உங்கள் வரி (sticker)" : "Your line (sticker)"}</span><span className="text-slate-400">{sticker.length}/30</span></div>
              <div className="flex gap-2">
                <input value={sticker} maxLength={30} onChange={(e) => setSticker(e.target.value)} placeholder={ta ? "உதா: Lucky day! 🍀" : "e.g. Lucky day! 🍀"} className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900" data-testid="story-sticker-input" />
                {sticker && <button onClick={() => setSticker("")} className="px-2 text-slate-400" data-testid="story-sticker-clear"><X className="w-4 h-4" /></button>}
              </div>
              <div className="flex gap-1.5 mt-1.5 overflow-x-auto">
                {(ta ? ["Lucky day! 🍀", "லக்ஷ்மி கடாட்சம் 🙏", "இன்று என் நாள் 🔥", "Jackpot! 💰"] : ["Lucky day! 🍀", "Blessed 🙏", "My day! 🔥", "Jackpot! 💰"]).map((s) => (
                  <button key={s} onClick={() => setSticker(s)} className="shrink-0 text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold" data-testid="story-sticker-suggestion">{s}</button>
                ))}
              </div>
            </div>
            <button onClick={go} disabled={busy} className="mt-4 w-full text-white font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-2" style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }} data-testid="story-theme-share">
              {busy && !stage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />}{ta ? "Instagram-ல் Share (படம்)" : "Share to Instagram (image)"}
            </button>
            <button onClick={goVideo} disabled={busy} className="mt-2 w-full font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-2 border-2 border-slate-900 text-slate-900 bg-white disabled:opacity-60" data-testid="story-video-share">
              {stage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
              {stage ? (stage.s === "record" ? `${ta ? "Video தயாராகிறது" : "Recording"} ${stage.p}%` : stage.s === "convert" ? (ta ? "MP4-ஆக மாற்றுகிறது…" : "Converting to MP4…") : (ta ? "Share…" : "Sharing…")) : (ta ? "5-sec Reel Video (confetti) 🎬" : "5-sec Reel video (confetti) 🎬")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
